import type { RouteNamedMap, _RouterTyped } from 'unplugin-vue-router'
import { canNavigate } from '@layouts/plugins/casl'
import { resolveActiveAppId } from '@/utils/activeApp'

const UNDER_MAINTENANCE_PATH = '/under-maintenance'
const MAINTENANCE_CACHE_TTL_MS = 15000

type PlatformStatus = {
  maintenance_mode: boolean
  maintenance_message?: string
  whitelabel_enabled?: boolean
}

let platformStatusCache: { checkedAt: number; data: PlatformStatus | null } = {
  checkedAt: 0,
  data: null,
}

const readPlatformStatus = async (): Promise<PlatformStatus | null> => {
  const now = Date.now()
  if (platformStatusCache.data && now - platformStatusCache.checkedAt < MAINTENANCE_CACHE_TTL_MS)
    return platformStatusCache.data

  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    }

    if (typeof window !== 'undefined' && window.location?.hostname)
      headers['X-Tenant-Domain'] = window.location.hostname

    const response = await fetch('/api/public/platform/status', {
      method: 'GET',
      credentials: 'include',
      headers,
    })

    if (!response.ok)
      return null

    const data = await response.json() as PlatformStatus

    platformStatusCache = {
      checkedAt: now,
      data,
    }

    return data
  }
  catch {
    return platformStatusCache.data
  }
}

export const setupGuards = (router: _RouterTyped<RouteNamedMap & { [key: string]: any }>) => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach(async to => {
    const userData = useCookie('userData').value as any
    const isAdmin = userData?.role === 'admin'
    const canManageTenantApps = !!userData?.can_manage_tenant_apps
    const hasPerpetualSubscription = !!userData?.has_perpetual_subscription
    const activeFeatures = Array.isArray(userData?.active_features) ? userData.active_features : []
    const isUnauthenticatedOnlyRoute = !!to.meta.unauthenticatedOnly
    const platformStatus = await readPlatformStatus()
    const maintenanceMode = !!platformStatus?.maintenance_mode

    if (maintenanceMode && !isAdmin && !isUnauthenticatedOnlyRoute && to.path !== UNDER_MAINTENANCE_PATH) {
      return {
        path: UNDER_MAINTENANCE_PATH,
        query: {
          redirect: to.fullPath,
        },
      }
    }

    if (!maintenanceMode && to.path === UNDER_MAINTENANCE_PATH) {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : '/'

      return redirect
    }

    /*
     * Self-registration crea un nuovo Tenant indipendente sulla piattaforma
     * AppForge (AuthController::register()), che poi vedrebbe i piani/
     * abbonamenti di AppForge stessa - su un dominio whitelabel e' un leak
     * del brand sottostante oltre che concettualmente sbagliato: gli
     * operatori di un tenant whitelabel vanno creati SOLO dal tenant stesso
     * (manualmente o via API), mai tramite un modulo pubblico che genera un
     * tenant nuovo. Bloccato qui (non solo nascosto il link in login.vue)
     * per coprire anche la navigazione diretta all'URL. Deciso con l'utente
     * il 2026-09-23.
     */
    if (to.name === 'register' && platformStatus?.whitelabel_enabled)
      return { name: 'login' }

    /*
     * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
     * Examples of public routes are, 404, under maintenance, etc.
     */
    if (to.meta.public)
      return

    /**
     * Check if user is logged in by checking if token & user data exists in local storage
     * Feel free to update this logic to suit your needs
     */
    const isLoggedIn = !!(useCookie('userData').value && useCookie('accessToken').value)

    /*
      If user is logged in and is trying to access login like page, redirect to home
      else allow visiting the page
      (WARN: Don't allow executing further by return statement because next code will check for permissions)

      Eccezione: una pagina con allowWithActiveSession (es. reset-password-v1,
      raggiunta da un link email one-time con token+email in query) deve
      restare sempre raggiungibile a prescindere da eventuali cookie di
      sessione residui nel browser (anche scaduti/di un altro account) -
      altrimenti chi apre il link viene rimbalzato a '/' e, con un token non
      piu' valido, ricade sul login invece di vedere il form di reset.
     */
    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn && !to.meta.allowWithActiveSession)
        return '/'
      else
        return undefined
    }

    /*
     * If user is NOT logged in and the route is not public/unauthenticatedOnly,
     * redirect to login. This prevents unauthenticated users from seeing any
     * internal page content.
     */
    if (!isLoggedIn) {
      return {
        name: 'login',
        query: {
          ...to.query,
          to: to.fullPath !== '/' ? to.path : undefined,
        },
      }
    }

    if ((to.name === 'settings-subscription'
      || to.name === 'settings-modules'
      || to.name === 'settings-whitelabel'
      || to.name === 'settings-team'
      || to.name === 'settings-reseller-provisioning'
      || to.name === 'settings-guides-reseller-api'
      || to.name === 'settings-backend-user-notifications'
      || to.name === 'settings-system-notification-templates'
      || to.name === 'settings-backend-smtp'
      || to.name === 'settings-backend-whatsender') && !canManageTenantApps) {
      return { name: 'portfolio' }
    }

    // Chi ha un abbonamento senza scadenza non ha nulla da gestire in questa pagina.
    if (to.name === 'settings-subscription' && hasPerpetualSubscription) {
      return { name: 'portfolio' }
    }

    if ((to.name === 'settings-whitelabel'
      || to.name === 'settings-reseller-provisioning'
      || to.name === 'settings-guides-reseller-api'
      || to.name === 'settings-backend-user-notifications'
      || to.name === 'settings-system-notification-templates'
      || to.name === 'settings-backend-smtp'
      || to.name === 'settings-backend-whatsender') && !activeFeatures.includes('tenant_whitelabel')) {
      return { name: 'portfolio' }
    }

    /*
     * App Context Guard:
     * Prevent clients from accessing app-specific routes without an active app selected.
     */
    if (userData && userData.role === 'client') {
      const appSpecificPrefixes = ['/apps/', '/settings/', '/dashboards/']
      const isAppRoute = appSpecificPrefixes.some(prefix => to.path.startsWith(prefix))
      
        if (isAppRoute
          && to.name !== 'settings-subscription'
          && to.name !== 'settings-whitelabel'
          && to.name !== 'settings-team'
          && to.name !== 'settings-reseller-provisioning'
          && to.name !== 'settings-guides-reseller-api'
          && to.name !== 'settings-backend-user-notifications'
          && to.name !== 'settings-system-notification-templates'
          && to.name !== 'settings-backend-smtp'
          && to.name !== 'settings-backend-whatsender') {
        // Fallback su userData.default_app_id quando la cache locale manca (dispositivo/browser
        // diverso da quello del login, storage svuotato, ecc.) - altrimenti un Operatore Backend con
        // accesso correttamente configurato viene rimbalzato al portfolio su OGNI pagina app-scoped,
        // stesso bug (e stessa causa) gia' corretto in resources/ts/navigation/vertical/client.ts il
        // 2026-10-20, qui piu' grave perche' blocca la navigazione invece di nascondere solo la sidebar.
        const activeAppId = localStorage.getItem('activeAppId') || resolveActiveAppId(userData)
        if (!activeAppId) {
          // No app selected, redirect to portfolio
          return { name: 'portfolio' }
        }
      }
    }

    /*
     * CASL permission check: if the user is logged in but doesn't have
     * the required ability for this route, redirect to not-authorized.
     */
    if (!canNavigate(to) && to.matched.length) {
      return { name: 'not-authorized' }
    }
  })
}
